// 라우터 backbone — render 디스패치, 하단 네비, FAB, in_progress 폴링.
//
// 페이지 렌더 함수들은 `setRouterPages`로 외부에서 주입받는다(DI). 이렇게
// 하면 라우터는 페이지 모듈을 import하지 않아 순환 의존을 피할 수 있고,
// 테스트나 다른 진입점에서도 페이지 셋만 갈아끼우면 재사용 가능.
//
// 폴링: status === "in_progress"인 이벤트 상세 화면에 30초마다 fetch해서
// 변화가 있으면 화면을 다시 그린다. 현재 라우트가 그 이벤트가 아니면 즉시
// 종료. 변화 감지는 "match_id:result_status:scoreA:scoreB:winner:players"의
// 단순 해시 비교.

import { render as preactRender } from "preact";
import { Icon } from "../ui/components/Icon";
import { mountPreact } from "../ui/preact-mount";
import { buildLoginCTA } from "../ui/loginCta";
import { state } from "../state";
import { navigate, refreshTopbar } from "./index";
import { api } from "../api";

export interface RouterPages {
  renderDashboard: (view: HTMLElement) => Promise<void> | void;
  renderEvents: (view: HTMLElement) => Promise<void> | void;
  renderStats: (view: HTMLElement) => Promise<void> | void;
  renderEventDetail: (view: HTMLElement, eventId: string) => Promise<void> | void;
  renderMembers: (view: HTMLElement) => Promise<void> | void;
  renderClubSettings: (view: HTMLElement) => Promise<void> | void;
  renderMemberLogin: (view: HTMLElement) => Promise<void> | void;
  renderInviteActivate: (view: HTMLElement, memberId: string, token: string) => Promise<void> | void;
  renderMyRecord: (view: HTMLElement) => Promise<void> | void;
  renderGuestEvent: (view: HTMLElement, eventId: string, token: string) => Promise<void> | void;
  renderRosterJoin: (view: HTMLElement, token: string) => Promise<void> | void;
}

let _pages: RouterPages | null = null;
export function setRouterPages(pages: RouterPages): void {
  _pages = pages;
}

// ---------- FAB ----------

export function removeFab(): void {
  const existing = document.querySelector(".cta-dock");
  if (existing) existing.remove();
}

export function renderFab(onClick: () => void, label = "＋ 새 일정 시작"): void {
  removeFab();
  const dock = mountPreact(
    <div class="cta-dock">
      <div class="cta-dock__inner">
        <button class="btn btn--accent btn--block" type="button" onClick={onClick}>{label}</button>
      </div>
    </div>,
  );
  document.body.append(dock);
}

// ---------- Auto-poll (in_progress event detail) ----------

let _pollTimer: ReturnType<typeof setInterval> | null = null;
let _pollHash: string | null = null;

export function stopPoll(): void {
  if (_pollTimer) {
    clearInterval(_pollTimer);
    _pollTimer = null;
  }
  _pollHash = null;
}

function eventHash(d: any): string {
  const matches = d.matches || [];
  return matches.map((m: any) =>
    `${m.match_id}:${m.result_status}:${m.side_a_score}:${m.side_b_score}:${m.winner_side}:${(m.player_ids || []).join(",")}`,
  ).join("|");
}

export function startPoll(eventId: string, initialDetail: any): void {
  stopPoll();
  if (initialDetail) _pollHash = eventHash(initialDetail);
  _pollTimer = setInterval(async () => {
    if (state.route.name !== "event" || state.route.id !== eventId) { stopPoll(); return; }
    try {
      const d = await api(`/api/v1/events/${eventId}`);
      if (d.event?.status !== "in_progress") { stopPoll(); return; }
      const newHash = eventHash(d);
      if (newHash === _pollHash) return;
      _pollHash = newHash;
      const view = document.getElementById("view");
      if (view && _pages) {
        const scrollY = view.scrollTop;
        view.innerHTML = "";
        await _pages.renderEventDetail(view, eventId);
        view.scrollTop = scrollY;
      }
    } catch (_) { /* swallow poll errors */ }
  }, 30000);
}

// ---------- Render entry ----------

let _renderGen = 0;

export async function render(): Promise<void> {
  if (!_pages) throw new Error("router pages not registered — call setRouterPages first");
  const gen = ++_renderGen;
  refreshTopbar();
  removeFab();
  stopPoll();
  const view = document.getElementById("view");
  if (!view) return;
  view.innerHTML = "";

  const r = state.route;
  const p = _pages;
  if (r.name === "home") await p.renderDashboard(view);
  else if (r.name === "events") await p.renderEvents(view);
  else if (r.name === "stats") await p.renderStats(view);
  else if (r.name === "event") {
    if (!state.session?.access_token) {
      view.append(buildLoginCTA({
        title: "일정 정보를 보려면 로그인해주세요",
        hint: "참가자 명단, 대진표, 점수 같은 일정 상세는 본인 PIN으로 로그인한 후에 볼 수 있어요.",
        pendingRoute: r,
      }));
    } else {
      await p.renderEventDetail(view, r.id);
    }
  }
  else if (r.name === "members") await p.renderMembers(view);
  else if (r.name === "club_settings") await p.renderClubSettings(view);
  else if (r.name === "member_login") await p.renderMemberLogin(view);
  else if (r.name === "invite_activate") await p.renderInviteActivate(view, r.memberId, r.token);
  else if (r.name === "my_record") await p.renderMyRecord(view);
  else if (r.name === "guest_event") await p.renderGuestEvent(view, r.eventId, r.token);
  else if (r.name === "roster_join") await p.renderRosterJoin(view, r.token);

  // 새 render가 이미 시작됐으면 그쪽이 view를 관리하므로 그냥 빠진다.
  // view.innerHTML=""을 치면 새 render가 이미 그려둔 내용을 와이프할 수 있음.
  if (gen !== _renderGen) return;
  renderBottomNav();
}

// ---------- Bottom nav ----------

const TABS: Array<{ key: string; label: string; icon: string }> = [
  { key: "home", label: "오늘", icon: "home" },
  { key: "events", label: "일정", icon: "calendar" },
  { key: "stats", label: "통계", icon: "bar-chart" },
];

const FULL_PAGE_ROUTES = new Set([
  "invite_activate", "member_login", "guest_event", "roster_join",
]);

function BottomNavTabs() {
  return (
    <>
      {TABS.map((t) => {
        const active = state.tab === t.key;
        return (
          <button
            key={t.key}
            type="button"
            class={"bottom-nav__item" + (active ? " bottom-nav__item--active" : "")}
            role="tab"
            aria-selected={active ? "true" : "false"}
            onClick={() => navigate({ name: t.key })}
          >
            <span class="bottom-nav__icon"><Icon name={t.icon} size={22} /></span>
            <span class="bottom-nav__label">{t.label}</span>
          </button>
        );
      })}
    </>
  );
}

export function renderBottomNav(): void {
  let nav = document.getElementById("bottom-nav");
  if (!nav) {
    nav = document.createElement("nav");
    nav.id = "bottom-nav";
    nav.className = "bottom-nav";
    nav.setAttribute("role", "tablist");
    document.body.append(nav);
  }
  // Hide bottom nav during full-page flows where it would be distracting.
  const hide = FULL_PAGE_ROUTES.has(state.route.name);
  nav.hidden = hide;
  // bottom-nav는 nav 자체가 Preact 컨테이너 — 숨길 때 innerHTML=""로 비우면
  // Preact의 _children 포인터가 stale로 남아 다시 보일 때 diff가 깨짐.
  // null을 render해서 정상 unmount.
  if (hide) {
    preactRender(null, nav);
    return;
  }
  preactRender(<BottomNavTabs />, nav);
}
